import { useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { getBlogEmpresarialBundle } from "../data/blogEmpresarialTranslations";
import { useEmpresasLanguage } from "../context/EmpresasLanguageContext";
import { resolveArticleCover } from "../utils/articleCoverImage";

const BASE_PATH = "/empresas/blog";

const BlogEmpresarialDetails = () => {
  const { id } = useParams();
  const { lang } = useEmpresasLanguage();
  const { articles, categories, ui } = useMemo(
    () => getBlogEmpresarialBundle(lang),
    [lang]
  );
  const category = categories[id];

  const categoryArticles = useMemo(
    () =>
      articles
        .filter((article) => article.category === id)
        .sort((a, b) => new Date(b.publishedAt) - new Date(a.publishedAt)),
    [articles, id]
  );

  if (!category) {
    return (
      <div className="min-h-[60vh] flex flex-col items-center justify-center gap-4 bg-[#f7fafc] px-6 text-center">
        <h1 className="text-3xl font-bold text-gray-900">{ui.categoryNotFoundTitle}</h1>
        <Link
          to={BASE_PATH}
          className="inline-flex items-center gap-2 px-5 py-3 bg-gray-900 text-white font-semibold rounded-full hover:bg-gray-800 transition"
        >
          {ui.backToBlog}
        </Link>
      </div>
    );
  }

  return (
    <div className="bg-white">
      <section className="bg-gradient-to-b from-[#e9e8f4] via-[#f5f3fc] to-white py-12 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-purple-800/70 flex flex-wrap items-center gap-2 mb-6">
            <Link to={BASE_PATH} className="font-semibold hover:text-purple-900">
              {ui.badge}
            </Link>
            <span>/</span>
            <span className="font-bold text-[#220365]">{category.title}</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-[#220365]">{category.title}</h1>
          <p className="mt-4 text-lg text-purple-900/80 max-w-2xl">{category.subtitle}</p>
        </div>
      </section>

      <section className="bg-gradient-to-b from-[#ddd6fe] via-[#e9e8f4] to-[#f5f3fc] py-14 sm:py-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categoryArticles.length === 0 ? (
              <div className="sm:col-span-2 lg:col-span-3 bg-white rounded-2xl p-10 text-center shadow-md">
                <p className="text-gray-600">{ui.categoryEmpty}</p>
              </div>
            ) : (
              categoryArticles.map((article) => {
                const cover = resolveArticleCover(article, categories);
                return (
                  <article
                    key={article.slug}
                    className="bg-white rounded-2xl shadow-md overflow-hidden flex flex-col h-full hover:shadow-lg transition"
                  >
                    <Link to={`${BASE_PATH}/${article.category}/${article.slug}`}>
                      <img
                        src={cover.src}
                        alt={cover.alt}
                        className="w-full h-44 object-cover"
                        loading="lazy"
                      />
                    </Link>
                    <div className="p-5 flex flex-col flex-1">
                      <p className="text-xs text-gray-500">
                        {category.title} | {article.date}
                      </p>
                      <Link to={`${BASE_PATH}/${article.category}/${article.slug}`}>
                        <h2 className="mt-2 text-lg font-bold text-[#220365] hover:text-purple-800 transition">
                          {article.title}
                        </h2>
                      </Link>
                      <p className="mt-2 text-sm text-gray-600 flex-1">{article.excerpt}</p>
                      <div className="mt-4 flex items-center justify-between text-xs text-gray-500">
                        <span>{article.author}</span>
                        <span>{article.readTime}</span>
                      </div>
                      <Link
                        to={`${BASE_PATH}/${article.category}/${article.slug}`}
                        className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900"
                      >
                        {ui.readArticle}
                      </Link>
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogEmpresarialDetails;
